import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import "./App.css";

import { useStateContext } from "./contexts/ContextProvider";
import { applyStatusBarColor } from "./hooks/useThemeColor";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { SnackbarProvider } from './contexts/SnackbarContext';
import { ToastProvider, BrandSplash } from "./components/ds";

import MainLayout from "./pages/MainLayout";
import Login from "./pages/Auth/Login";
import LandingPage from "./pages/Auth/LandingPage";
//* Dev-only design-system showcase (Phase 2 primitives) — route gated below.
import DsShowcase from "./pages/_dev/DsShowcase";
//* Dev-only mobile primitive layer showcase (Phase 1 mobile primitives) — route gated below.
import DsMobileShowcase from "./pages/_dev/DsMobileShowcase";
import ProfileCompletion from "./pages/Auth/ProfileCompletion";
import ProtectedRoute from "./components/ProtectedRoute";
import LoginOverlay from "./components/LoginOverlay";
import BrokerSessionCallback from "./pages/BrokerPlatforms/Login/BrokerSessionCallback";
import Profile from "./pages/Auth/Profile";
import StrategyBuilder from "./pages/Strategies/Modern/StrategyBuilder";
import PopOutDashboard from "./pages/Strategies/Modern/StrategyBuilder/components/PopOutDashboard";
import { WithLayout } from "./components";
import SessionTimeoutMonitor from "./components/SessionTimeoutMonitor";
import SimpleWebSocketTest from "./test/SimpleWebSocketTest";

//--> Plays the brand "welcome" reveal once, right after a fresh sign-in.
//    Must live inside <AuthProvider> to read auth state.
function BrandWelcomeGate() {
  const { isAuthenticated } = useAuth();
  const [name, setName] = useState(null);
  useEffect(() => {
    if (!isAuthenticated) return;
    let flag = null;
    try { flag = sessionStorage.getItem("ds-welcome"); } catch {}
    if (flag !== null) {
      try { sessionStorage.removeItem("ds-welcome"); } catch {}
      setName(flag);
    }
  }, [isAuthenticated]);
  if (name === null) return null;
  return <BrandSplash mode="welcome" name={name} onDone={() => setName(null)} />;
}

//--> Cold-start brand splash — plays once per browser session on first load.
//    Load-aware: it dismisses as soon as auth init finishes (ready = !loading),
//    with a floor so it can't flicker and a cap so it can't hang. Lives inside
//    <AuthProvider> to read `loading`; no artificial delay.
function BrandColdStartGate() {
  const { loading } = useAuth();
  const [show, setShow] = useState(() => {
    try { return !sessionStorage.getItem("ds-splash-seen"); } catch { return false; }
  });
  const dismiss = () => {
    try { sessionStorage.setItem("ds-splash-seen", "1"); } catch {}
    setShow(false);
  };
  if (!show) return null;
  return <BrandSplash mode="splash" ready={!loading} onDone={dismiss} />;
}

// "/" is the marketing landing for signed-OUT visitors only. Signed-in users
// dropped here (e.g. reopening the installed PWA) should go straight into the
// app instead of the "go to dashboard" landing. Rendered as a route element so
// it's inside <AuthProvider> and can read useAuth. While auth is still
// initializing we render nothing (the cold-start splash covers this) so we
// neither flash the landing nor redirect before auth is known.
function LandingOrDashboard() {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return null;
  if (isAuthenticated) return <Navigate to="/Overview" replace />;
  return <LandingPage />;
}

const App = () => {
  const { MUItheme } = useStateContext();
  const {
    setCurrentColor,
    setCurrentMode,
    currentMode,
    activeMenu,
    currentColor,
    themeSettings,
    setThemeSettings,
  } = useStateContext();

  // State for login overlay
  const [showLoginOverlay, setShowLoginOverlay] = useState(false);

  // Apply dark mode class to document root (html element) for portals/modals
  useEffect(() => {
    if (currentMode === "Dark") {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
    //--> Keep DS tokens ([data-theme]) in lockstep with Tailwind's .dark class
    document.documentElement.dataset.theme = currentMode === "Dark" ? "dark" : "light";

    //--> Tint the phone status bar to match the app background (native-app feel).
    //    Routed through applyStatusBarColor so a brand surface's forced pin (the
    //    always-dark landing / splash via useThemeColor) wins over the app theme
    //    even though this parent effect runs after those child effects.
    applyStatusBarColor();

    // Dynamic Favicon Switching
    const favicon = document.getElementById("favicon");
    if (favicon) {
      if (currentMode === "Light") { // Assuming 'Light' is the exact string (StateContext default is 'Dark')
         // In ContextProvider, toggle sets it to "Light" or "Dark" (Capitalized)
         favicon.href = process.env.PUBLIC_URL + "/favicon-light.ico";
      } else {
         favicon.href = process.env.PUBLIC_URL + "/favicon.ico";
      }
    }
  }, [currentMode]);

  return (
    <ThemeProvider theme={MUItheme}>
      <ToastProvider>
        <SnackbarProvider>
        <AuthProvider>
          <BrandColdStartGate />
          <BrandWelcomeGate />
          <SessionTimeoutMonitor />
          <div className={currentMode === "Dark" ? "dark" : ""}>
            {/* <div> */}
            <BrowserRouter>
              {/* Login Overlay - appears on top of any page when needed */}
              <LoginOverlay
                isOpen={showLoginOverlay}
                onClose={() => setShowLoginOverlay(false)}
              />

              <Routes>
                {/* Public routes that don't require authentication */}
                {/* Signed-in users skip the landing and go straight to /Overview */}
                <Route path="/" element={<LandingOrDashboard />} />
                <Route path="/login" element={<Login />} />
                {/* Dev-only DS primitives showcase — not registered in production */}
                {process.env.NODE_ENV !== "production" && (
                  <Route path="/ds" element={<DsShowcase />} />
                )}
                {/* Dev-only DS mobile primitive layer showcase — not registered in production */}
                {process.env.NODE_ENV !== "production" && (
                  <Route path="/ds/mobile" element={<DsMobileShowcase />} />
                )}

                {/* Special routes that require authentication but not profile completion */}
                <Route
                  element={<ProtectedRoute requireProfileCompletion={false} />}
                >
                  <Route
                    path="/profile-completion"
                    element={<ProfileCompletion />}
                  />
                </Route>

                {/* Profile route - protected but with layout */}
                <Route
                  element={<ProtectedRoute requireProfileCompletion={true} />}
                >
                  <Route
                    path="/profile"
                    element={
                      <WithLayout>
                        <Profile />
                      </WithLayout>
                    }
                  />
                </Route>

                {/* API callback routes */}
                <Route
                  path="/websocket-test"
                  element={<SimpleWebSocketTest />}
                />
                <Route
                  path="/callback/:userId/:brokerAccountId"
                  element={<BrokerSessionCallback />}
                />
                <Route
                  path="/callback"
                  element={<BrokerSessionCallback />}
                />

                {/* Protected routes that require authentication and profile completion */}
                <Route
                  element={<ProtectedRoute requireProfileCompletion={true} />}
                >
                  <Route path="/*" element={<MainLayout />} />
                  <Route path="/strategies/create" element={<WithLayout><StrategyBuilder/></WithLayout>} />
                  <Route path="/strategies/edit/:strategyId" element={<WithLayout><StrategyBuilder/></WithLayout>} />
                  <Route path="/strategies/edit/:strategyId/version/:versionId" element={<WithLayout><StrategyBuilder/></WithLayout>} />
                  <Route path="/strategies/:strategyId/dashboard-popout" element={<PopOutDashboard />} />
                </Route>

                {/* Redirect any other routes to Overview */}
                <Route path="*" element={<Navigate to="/Overview" replace />} />
              </Routes>
            </BrowserRouter>
          </div>
        </AuthProvider>
        </SnackbarProvider>
      </ToastProvider>
    </ThemeProvider>
  );
};

export default App;
